/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, March 2022
 * @flow
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './Items.module.css';
import { searchIcon } from '../../../../assets/images';
import Toggle from '../../../../components/Toggle';
import Checkbox from '../../../../components/Checkbox';
import strings from '../../../../globalization';
import LeftSection from './LeftSection';
import FireboardUtils from '../../../../util/fireboard-util';

export default function OutOfStockItems(props) {
  const {
    outOfStockItems,
    outOfStockSearchList,
    onReloadOutOfStock,
    onSearchOutOfStock,
    includeUnavailable,
    onToggleIncludeUnavailable,
    onUpdateItemStock,
  } = props;
  const [searchedValue, setSearchedValue] = useState('');
  const [sortByName, setSortByName] = useState(true);

  const onSearch = () => {
    if (searchedValue.length === 0) return;
    onSearchOutOfStock && onSearchOutOfStock(searchedValue);
    setSearchedValue('');
  };

  const onItemClick = item => {
    onUpdateItemStock && onUpdateItemStock(item);
  };

  const onSubmit = e => {
    e.preventDefault();
    onSearch();
  };

  const onChangeSortByName = () => {
    setSortByName(prevSortByName => !prevSortByName);
  };

  const pad = (value) => {
    let str = String(value);
    let size = 10;
    while (size > 0) {
      str = '0' + str;
      size--;
    }
    return str;
  };

  const sortNameFunc = (a, b) => {
    const name1 = pad(a.name ? a.name : '').toLowerCase();
    const name2 = pad(b.name ? b.name : '').toLowerCase();
    if (name1 < name2) {
      return -1;
    }
    if (name1 > name2) {
      return 1;
    }
    return 0;
  };

  const sortPOSIDFunc = (a, b) => {
    const posId1 = pad(a.posId ? a.posId : '').toLowerCase();
    const posId2 = pad(b.posId ? b.posId : '').toLowerCase();
    if (posId1 < posId2) {
      return -1;
    }
    if (posId1 > posId2) {
      return 1;
    }
    return 0;
  };

  const searchedItemsList = sortByName
    ? outOfStockSearchList.sort(sortNameFunc)
    : outOfStockSearchList.sort(sortPOSIDFunc);

  return (
    <div className={styles.mainWrapper}>
      <LeftSection
        outOfStockItems={outOfStockItems}
        onReloadOutOfStock={onReloadOutOfStock}
        onUpdateItemStock={onUpdateItemStock}
      />
      <div className={styles.rightWrapper}>
        <div className={styles.wrapper}>
          <div className={styles.inputWrapper}>
            <SearchInput
              value={searchedValue}
              onChange={val => setSearchedValue(val)}
              onSubmit={onSubmit}
              search={onSearch}
            />
            <CheckBox checked={includeUnavailable} onChange={onToggleIncludeUnavailable} />
          </div>
          <Switch selected={sortByName} onToggle={onChangeSortByName} />
        </div>
        <div className={styles.container}>
          {searchedItemsList.map(item => {
            const isOutOfStock = outOfStockItems.find(i => i.id === item.id);
            const name = item.posId ? `${item.name} (${item.posId})` : item.name;
            let backInStockDate;
            if (item?.backInStockDate) {
              backInStockDate = `${FireboardUtils.convertedDate(item?.backInStockDate)} ${FireboardUtils.getTimeInHrsMins(item?.backInStockDate)}`;
            }
            return (
              <div
                key={`searchItem-${item.id}`}
                className={classNames({ [styles.cell]: true, [styles.unavailableItem]: !item.available })}
                onClick={() => onItemClick(item)}>
                <div className={[styles.items, isOutOfStock ? styles.stockWidth : styles.inStockWidth].join(' ')}>{name}</div>
                {backInStockDate && <div className={styles.inStockDate}>{`(In stock on ${backInStockDate})`}</div>}
                {isOutOfStock ? <span className={styles.outOfStockRep}>86</span> : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  );
};

OutOfStockItems.propTypes = {
  outOfStockItems: PropTypes.array,
  outOfStockSearchList: PropTypes.array,
  onReloadOutOfStock: PropTypes.func,
  onSearchOutOfStock: PropTypes.func,
  includeUnavailable: PropTypes.bool,
  onToggleIncludeUnavailable: PropTypes.func,
  onUpdateItemStock: PropTypes.func,
};

OutOfStockItems.defaultProps = {
  outOfStockItems: [],
  outOfStockSearchList: [],
  onReloadOutOfStock: () => { },
  onSearchOutOfStock: () => { },
  includeUnavailable: false,
  onToggleIncludeUnavailable: () => { },
  onUpdateItemStock: () => { },
};

const SearchInput = ({ value, onChange, onSubmit, search }) => {
  return (
    <div className={styles.searchWrapper}>
      <form onSubmit={onSubmit}>
        <input
          type="text"
          value={value}
          placeholder={strings.searchWithPosIdOrName}
          onChange={event => onChange(event.target.value)}
          className={styles.textInput} />
      </form>
      <img src={searchIcon} alt="search" className={styles.searchIcon} onClick={search} />
    </div>
  );
};

const Switch = ({ onToggle, selected }) => {
  return (
    <div className={styles.switchWrapper}>
      <span className={styles.switchName}>{strings.sortByName}</span>
      <Toggle onClick={onToggle} selected={selected} />
      <span className={styles.switchName}>{strings.sortByPOSID}</span>
    </div>
  );
};

const CheckBox = ({ checked, onChange }) => {
  return (
    <div className={styles.checkboxWrapper}>
      <Checkbox checked={checked} onChange={onChange} />
      <span className={styles.switchName}>{strings.includeUnavailable}</span>
    </div>
  );
};
